import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { t } from '@fairstone/ui/core/providers/Intl/locales/translate';
import { Box } from '@mui/material';
import atatus from 'atatus';
import { Auth } from 'aws-amplify';
import { LIMIT_EXCEEDED_EXCEPTION, UNAUTHORIZED_EXCEPTION } from 'constant';
import useFormFields from 'hooks/useFormFields';

import ErrorView from 'components/errors/ErrorView';
import EditProfileWrapper from 'components/forms/profile/EditProfileWrapper';
import PasswordConfirmation from 'components/inputs/PasswordConfirmation';
import CustomModal from 'components/modals/CustomModal';
import OverlaySpinner from 'components/spinner/OverlaySpinner';
import { PasswordInput, PrimaryButtonSto } from 'components/sto';
import { AppRoutes } from 'routes';
import { updateProfile } from 'services/AccountService';

const ProfilePasswordPage = () => {
    const initialFormValues = {
        newPassword: {
            value: '',
        },
        oldPassword: {
            value: '',
        },
    };

    const { formFields, setFormField } = useFormFields(initialFormValues);
    const { newPassword, oldPassword } = formFields;
    const [apiError, setApiError] = useState(null);
    const history = useHistory();
    const [isFetching, setIsFetching] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { formatMessage, locale } = useIntl();
    const [modal, setModal] = useState({
        visible: false,
    });

    const handleApiError = (error) => {
        switch (error?.name) {
            case UNAUTHORIZED_EXCEPTION:
                setApiError('pages.profile.edit.error.incorrect.password');
                break;
            case LIMIT_EXCEEDED_EXCEPTION:
                setApiError('pages.profile.edit.error.limit.exceeded');
                break;
            default:
                setModal({
                    modalType: 'fail',
                    okText: 'general.ok',
                    onOk: handleGoBackOnError,
                    title: 'pages.profile.edit.failed',
                    visible: true,
                });
                break;
        }
    };

    const handleApiSuccess = () => {
        setModal({
            modalType: 'success',
            okText: 'general.ok',
            onOk: handleGoBackOnSuccess,
            title: 'pages.profile.edit.password.updated',
            visible: true,
        });
    };

    const handleCurrentPassChange = (event) => {
        const { value } = event.target;
        setFormField('oldPassword', {
            isValid: !!value.length,
            value: value,
        });
    };

    const handleGoBackOnSuccess = () => {
        setModal({ visible: false });
        history.push(`/${locale}/${formatMessage({ id: 'tab.label.profile' }).toLowerCase()}`);
    };

    const handleGoBackOnError = () => {
        setModal({ visible: false });
    };

    const handleNewPassChange = (password) => {
        setFormField('newPassword', {
            isValid: password.passwordState,
            value: password.passwordValue,
        });
    };

    const handleSave = async (event) => {
        try {
            event.preventDefault();
            setIsSubmitting(true);
            atatus.startTransaction('UpdateProfilePassword');
            setIsFetching(true);
            const user = await Auth.currentAuthenticatedUser();
            await Auth.changePassword(user, oldPassword.value, newPassword.value);
            handleApiSuccess();
            updateProfile({ password: '' });
            atatus.endTransaction('UpdateProfilePassword');
        } catch (error) {
            if (error?.name !== LIMIT_EXCEEDED_EXCEPTION) setIsFetching(false);
            handleApiError(error);
            atatus.failTransaction('UpdateProfilePassword');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSubmitting)
        return (
            <OverlaySpinner>
                <p>{t('spinner.submitting.information')}</p>
                <p>{t('spinner.message.2')}</p>
            </OverlaySpinner>
        );

    return (
        <EditProfileWrapper goBack={AppRoutes.profile} title="pages.profile.title.password">
            <Box className="profile-change-password-container">
                <form autoComplete="off" className="profile-change-password-content" onSubmit={handleSave}>
                    {apiError && <ErrorView error={apiError} />}
                    <PasswordInput
                        aria-label="pages.profile.edit.password.current.placeholder"
                        errorMessage="pages.profile.edit.error.blank.password"
                        id="current-password"
                        isValid={oldPassword.isValid}
                        label="pages.profile.edit.password.current"
                        name="currentPassword"
                        onChange={handleCurrentPassChange}
                        placeholder="pages.profile.edit.password.current.placeholder"
                        value={oldPassword.value}
                    />
                    <PasswordConfirmation
                        confirmLabel="pages.profile.edit.password.confirm"
                        confirmPlaceHolder="pages.profile.edit.password.confirm.placeholder"
                        newLabel="pages.profile.edit.password.new"
                        newPlaceHolder="pages.profile.edit.password.new.placeholder"
                        onChange={handleNewPassChange}
                        password={{
                            passwordState: newPassword.isValid,
                            passwordValue: newPassword.value,
                        }}
                    />

                    <PrimaryButtonSto
                        disabled={isFetching || !oldPassword.isValid || !newPassword.isValid}
                        labelKey="general.save"
                        type="submit"
                    />
                </form>
                <CustomModal
                    modalType={modal.modalType}
                    okText={modal.okText}
                    onOk={modal.onOk}
                    visible={modal.visible}
                >
                    <p>
                        <FormattedMessage id={modal.title} />
                    </p>
                </CustomModal>
            </Box>
        </EditProfileWrapper>
    );
};

export default ProfilePasswordPage;
